import React, { createContext, useContext, useMemo } from "react";
import useSWR from "swr";
import { StaticPageApiUrls, ExamInfoApiUrls } from "../Api/Auth/ApiUrls";
import { noAuthfetcher } from "../Api/swrConfig";

const ContactContext = createContext({
  contact: null,
  footerPages: [],
  loading: true,
  error: null,
});

const FALLBACK_EMAIL = "support@roadtofe.com";

export const ContactProvider = ({ children }) => {
  const {
    data: contactData,
    error: contactError,
  } = useSWR(StaticPageApiUrls.info("contact-us"), noAuthfetcher, {
    revalidateOnFocus: false,
  });

  const {
    data: footerData,
    error: footerError,
  } = useSWR(ExamInfoApiUrls.list(""), noAuthfetcher, {
    revalidateOnFocus: false,
  });

  const value = useMemo(() => {
    const hasContactError = Boolean(contactError);
    if (hasContactError) {
      return {
        contact: null,
        footerPages: [],
        loading: false,
        error: contactError,
      };
    }

    const rawContent = (() => {
      try {
        return contactData?.data?.content
          ? JSON.parse(contactData.data.content)
          : {};
      } catch (err) {
        console.warn("Failed to parse contact content", err);
        return {};
      }
    })();

    const socialLinks = rawContent?.socialLinks || rawContent || {};

    const contact = contactData?.data
      ? {
          heroTitle: rawContent?.heroTitle || contactData.data.title || "Contact Us",
          heroSubtitle:
            rawContent?.heroSubtitle ||
            contactData.data.headerDescription ||
            rawContent?.subtitle ||
            "Find Us",
          backgroundImage:
            rawContent?.backgroundImage ||
            rawContent?.heroImage ||
            contactData.data.image ||
            "",
          bodyContent:
            rawContent?.bodyContent ||
            rawContent?.content ||
            contactData.data.bodyDescription ||
            "",
          socialLinks: {
            linkedin: socialLinks?.linkedin || "",
            facebook: socialLinks?.facebook || "",
            youtube: socialLinks?.youtube || "",
            email: socialLinks?.email || rawContent?.email || FALLBACK_EMAIL,
            phone: socialLinks?.phone || rawContent?.phone || "",
            address: socialLinks?.address || rawContent?.address || "",
          },
        }
      : null;

    const footerPages = Array.isArray(footerData?.data)
      ? footerData.data.map((item) => ({
          title: item.title,
          slug: item.slug,
          description: item.description || "",
        }))
      : [];

    return {
      contact,
      footerPages,
      loading: !contactData && !footerData && !footerError,
      error: footerError || null,
    };
  }, [contactData, contactError, footerData, footerError]);

  return (
    <ContactContext.Provider value={value}>{children}</ContactContext.Provider>
  );
};

export const useContact = () => useContext(ContactContext);
